import { Observable, of, throwError, zip } from "rxjs"
import { catchError, switchMap } from "rxjs/operators"
import _ from "lodash"

import { Alias, BatchResource } from "@skinsoft/types"
import { FailedToDetermineItemsToSyncError } from "./errors"
import { BatchApiService } from "@skinsoft/api-services"
import { AliasDataStore, AuditCampaignDataStore } from "@skinsoft/entity"

function getCollaboratorId(): Observable<Alias> {
    const userId = localStorage.getItem("userId")
    if(userId) {
        return AliasDataStore.getInstance().getCollaboratorByUserId(userId!) as Observable<Alias>
    }

    return of(null).pipe(
        switchMap(x => {
            return throwError(new Error)
        })
    ) as Observable<Alias>
}

function getUserAuditProjects() {
    const userId = localStorage.getItem("userId")

    return getCollaboratorId().pipe(
        switchMap(collaboratorAlias => {
            if(collaboratorAlias?.id) {
                const uri = "/indexSearch/search"

                // campaign workflow status equals to 'inProgress'
                const inProgressStatusCriteria = {
                    type: "requestSearch",
                    leftCriteria: {
                        operator: "equals", 
                        value: { 
                            "text": "ui.lifecycle-state.inProgress", 
                            type: "text" 
                        }, 
                        entityField: "5e2713835111f87e825ede00", 
                        type: "criteria"                
                    },
                    linkOperator: 'OR',
                    rightCriteria: {
                        operator: "equals", 
                        value: { 
                            "text": "inProgress", 
                            type: "text" 
                        }, 
                        entityField: "5e2713835111f87e825ede00", 
                        type: "criteria"
                    },
                }          
                
                const advancedSearches = [{
                    // campaign workflow status equals to 'inProgress'
                    leftCriteria: inProgressStatusCriteria,
                    linkOperator: "AND",
                    // authorizedAuditor equals to current collaborator
                    rightCriteria: { 
                        operator: "equals", 
                        value: { 
                            completeSearch: false, 
                            refId: collaboratorAlias.id, 
                            type: "ref" 
                        }, 
                        entityField: "5e2713835111f87e825ede23", 
                        type: "criteria" 
                    },
                    type: "requestSearch",
                }]

                const searchQuery = {
                    advancedSearches: advancedSearches,
                    searchOnArchived: false,
                    searchOnVersioned: false
                }

                return AuditCampaignDataStore.getInstance().searchAllOnline(
                    searchQuery,
                )
            }

            throw new Error()
        }),
    )
}

export function getItemsToSyncAuditCampaign() {
    return getUserAuditProjects().pipe(
        switchMap(searchResult => {
            if(searchResult?.allData?.length) {
                let itemsIdsToSync: string[] = []

                for(const auditProject of searchResult.allData) {
                    const items = _.get(auditProject, "workflowSchema.linkableItems", [])
                    const itemsId = items.map(linkableItemRef => {
                        if(linkableItemRef?.linkableItem?.ref) {
                            return linkableItemRef?.linkableItem?.ref
                        }
                        return null
                    })
                    .filter(itemId => {
                        return itemId != null
                    })

                    itemsIdsToSync = itemsIdsToSync.concat(itemsId)
                }

                return of(itemsIdsToSync)
            }

            return of([])
        }),
        catchError(err => {
            return throwError(new FailedToDetermineItemsToSyncError())
        }),
    )
}

export function getBatchesToSync(): Observable<BatchResource[]> {
    const userId = localStorage.getItem("userId")

    if(userId) {
        const batchAPIService = new BatchApiService()
        return batchAPIService.getAllBatchesSharedWithUser(userId).pipe(
            switchMap(res => {
                let sharedBatchToSync = []
                if(res?._embedded?.batchResources?.length) {
                    sharedBatchToSync = res._embedded.batchResources.filter(batch => {
                        if(batch?.batchSchema?.isSynchronisedWithReporter) {
                            return true
                        }
                        return false
                    })
                }

                return zip(
                    of(sharedBatchToSync),
                    batchAPIService.getAllUserBatches(userId)
                ) 
            }),
            switchMap(([sharedBatchToSync, userBatchResponse]) => {
                let allBatchToSync = []

                if(sharedBatchToSync?.length) {
                    allBatchToSync = allBatchToSync.concat(sharedBatchToSync)
                }
                if(userBatchResponse) {
                    if(userBatchResponse?._embedded?.batchResources?.length) {
                        const userBatchToSync = userBatchResponse._embedded.batchResources.filter(batch => {
                            if(batch?.batchSchema?.isSynchronisedWithReporter) {
                                return true
                            }
                            return false
                        })

                        if(userBatchToSync?.length) {
                            allBatchToSync = allBatchToSync.concat(userBatchToSync)
                        }
                    }
                }

                return of(allBatchToSync)
            })
        )
    }

    return throwError(new Error())
}

export function getItemsIdsInBatches(batchesToSync: BatchResource[]): string[] {
    return _.uniq(
        batchesToSync.filter(batch => {
            return batch?.batchSchema?.recordList?.length
        })
        .flatMap(batch => {
            return batch.batchSchema!.recordList
        })
        .flatMap(record => record?.id)
        .filter(id => id != null)
    ) as string[]
}

export function getItemsToSyncBatches(): Observable<string[]> {
    return getBatchesToSync().pipe(
        switchMap(batches => {
            return of(
                getItemsIdsInBatches(batches)
            )
        }),
        catchError(err => {
            return throwError(new FailedToDetermineItemsToSyncError())
        }),
    )
}